/* global __BUILD_ID__ */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnsavedWork } from '../hooks/useUnsavedWork.jsx';
import Modal from './ui/Modal.jsx';
import Button from './ui/Button.jsx';
import { AlertTriangle } from 'lucide-react';

// Unmissable top banner for two "you need to re-load / re-login" states:
//   1. A newer version has been deployed than the one this tab is running.
//   2. The sign-in session has genuinely expired (a 401 that survived the
//      API layer's silent token-refresh retry).
//
// Deliberately NEVER force-reloads or force-logs-out — that's how you lose
// someone's half-entered data. The silent token refresh in api.js still
// fixes the easy cases invisibly; this only surfaces when a real reload /
// re-login is required, and the user clicks when THEY are ready.

const POLL_MS = 5 * 60 * 1000;
const CURRENT = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';

export default function AppStatusBanner() {
  const [newVersion, setNewVersion] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const promptedRef = useRef(false);
  const { requestReload, reloadModalOpen, closeReloadModal, forceReload, getDirtyItems } = useUnsavedWork();
  const navigate = useNavigate();

  // Session-expired signal from api.js. Ignore it when there's no signed-in
  // account (e.g. sitting on the login page) so we don't nag pre-login.
  useEffect(() => {
    const onExpired = () => {
      const msal = window.__msalInstance;
      if (msal?.getAllAccounts && msal.getAllAccounts().length === 0) return;
      setSessionExpired(true);
    };
    window.addEventListener('prohr:session-expired', onExpired);
    return () => window.removeEventListener('prohr:session-expired', onExpired);
  }, []);

  // New-version check — poll the deployed version.json and compare the build
  // id to the one baked into this bundle. Production only (no version.json
  // in dev). Re-checks on an interval and whenever the tab regains focus.
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    let cancelled = false;
    async function check() {
      if (cancelled || promptedRef.current) return;
      try {
        const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.buildId && data.buildId !== CURRENT) {
          promptedRef.current = true;
          setNewVersion(true);
        }
      } catch { /* offline / transient — try again next tick */ }
    }
    check();
    const id = setInterval(check, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Reload goes through the unsaved-work registry: if any form has dirty,
  // unsaved changes, requestReload() opens a confirmation modal listing
  // them instead of reloading straight away and silently losing work.
  const reload = requestReload;
  const reSignIn = useCallback(() => {
    const msal = window.__msalInstance;
    if (msal?.loginRedirect) {
      try { msal.loginRedirect(); return; } catch { /* fall through */ }
    }
    window.location.reload();
  }, []);

  // Session-expired is more urgent (actions will fail until re-auth), so it
  // wins if somehow both are set.
  const banner = sessionExpired ? (
    <Banner
      tone="danger"
      text="Your session has expired. Sign in again to keep working — this page stays open until you do, so finish what you can first."
      actionLabel="Sign in again"
      onAction={reSignIn}
    />
  ) : newVersion ? (
    <Banner
      tone="primary"
      text="A new version of KSA ProHR is available."
      actionLabel="Reload"
      onAction={reload}
    />
  ) : null;

  return (
    <>
      {banner}
      <ReloadConfirmModal
        isOpen={reloadModalOpen}
        onClose={closeReloadModal}
        items={getDirtyItems()}
        onGoTo={(href) => { closeReloadModal(); navigate(href); }}
        onForceReload={forceReload}
      />
    </>
  );
}

// Shown when the user asks to reload (via the update banner, or any other
// caller of requestReload()) while at least one form has unsaved changes.
// Lets them jump to a form to save it first, or force the reload anyway.
function ReloadConfirmModal({ isOpen, onClose, items, onGoTo, onForceReload }) {
  if (!isOpen) return null;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="You have unsaved work"
      width="480px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={onForceReload}>Update anyway</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
          <span>Reloading now will discard the changes below. Save your work first, or continue anyway.</span>
        </div>
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3.5 py-2.5"
            >
              <span className="text-sm font-medium truncate">{item.label || item.id}</span>
              {item.href && (
                <Button size="sm" variant="secondary" onClick={() => onGoTo(item.href)}>
                  Go save
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function Banner({ tone, text, actionLabel, onAction }) {
  const bg = tone === 'danger' ? 'bg-destructive' : 'bg-primary';
  return (
    <div className={`fixed inset-x-0 top-0 z-[200] ${bg} text-white shadow-lg`}>
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2.5 text-sm">
        <span className="font-medium leading-snug">{text}</span>
        <button
          onClick={onAction}
          className="ml-auto shrink-0 rounded-md bg-white/20 px-3 py-1 text-xs font-semibold transition-colors hover:bg-white/30"
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}
